import { useState, useEffect, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { Undo2, Clock, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export interface ActionHistoryEntry {
  id: string;
  action: string;
  sql: string;
  timestamp: string;
  reverseSql?: string;
}

const STORAGE_KEY = "linabase_action_history";

function loadHistory(): ActionHistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: ActionHistoryEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, 100)));
  } catch {
    // sessionStorage full or unavailable
  }
}

export function pushActionHistory(entry: Omit<ActionHistoryEntry, "id" | "timestamp">) {
  const entries = loadHistory();
  entries.unshift({
    ...entry,
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
  });
  saveHistory(entries);
}

export function clearActionHistory() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function ActionHistorySheet({
  open,
  onOpenChange,
  onRefresh,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefresh: () => void;
}) {
  const [entries, setEntries] = useState<ActionHistoryEntry[]>([]);
  const [undoing, setUndoing] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setEntries(loadHistory());
    }
  }, [open]);

  const handleUndo = useCallback(async (entry: ActionHistoryEntry) => {
    if (!entry.reverseSql) return;
    setUndoing(entry.id);
    try {
      const res = await fetch("/api/database/execute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sql: entry.reverseSql }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        toast.error("Undo failed", { description: data.error || "Unknown error" });
      } else {
        toast.success("Action undone");
        // Remove the entry and add the undo as a new action
        const updated = loadHistory().filter((e) => e.id !== entry.id);
        updated.unshift({
          id: crypto.randomUUID(),
          action: `Undo: ${entry.action}`,
          sql: entry.reverseSql,
          timestamp: new Date().toISOString(),
        });
        saveHistory(updated);
        setEntries(updated);
        onRefresh();
      }
    } catch (err: any) {
      toast.error("Undo failed", { description: err.message || "Network error" });
    } finally {
      setUndoing(null);
    }
  }, [onRefresh]);

  function handleClear() {
    clearActionHistory();
    setEntries([]);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Action History
          </SheetTitle>
          <SheetDescription>
            Recent DDL operations performed in this session.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="h-8 w-8 text-muted-foreground/30" />
              <p className="mt-2 text-sm text-muted-foreground">No actions recorded yet.</p>
              <p className="text-xs text-muted-foreground/60">DDL operations will appear here as you work.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {entries.map((entry) => (
                <div key={entry.id} className="px-4 py-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium truncate">{entry.action}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                      {new Date(entry.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <pre className="text-xs font-mono text-muted-foreground bg-secondary/50 rounded px-2 py-1.5 overflow-x-auto whitespace-pre-wrap break-all max-h-20">
                    {entry.sql}
                  </pre>
                  {entry.reverseSql && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs gap-1"
                      disabled={undoing === entry.id}
                      onClick={() => handleUndo(entry)}
                    >
                      {undoing === entry.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Undo2 className="h-3 w-3" />
                      )}
                      Undo
                    </Button>
                  )}
                  {!entry.reverseSql && (
                    <Badge variant="secondary" className="text-[10px]">No undo available</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {entries.length > 0 && (
          <div className="px-6 py-3 border-t border-border">
            <Button variant="outline" size="sm" className="w-full text-xs gap-1" onClick={handleClear}>
              <Trash2 className="h-3 w-3" />
              Clear History
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
